// eslint-disable-next-line import/no-cycle
import useJwt from '@/auth/jwt/useJwt'
import router from '@/router'
import { logEvent } from 'firebase/analytics'
import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
    // Will be used by this service for making API calls
    axiosIns = null

    // jwtConfig <= Will be used by this service
    jwtConfig = { ...jwtDefaultConfig }

    // For Refreshing Token
    isAlreadyFetchingAccessToken = false

    // For Refreshing Token
    subscribers = []

    // 401 sonrası /me doğrulaması devam ederken tutulan promise
    authCheckPromise = null

    constructor(axiosIns, jwtOverrideConfig) {
      this.axiosIns = axiosIns
      this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

      // Request Interceptor
      this.axiosIns.interceptors.request.use(
        config => {
          // eslint-disable-next-line no-param-reassign
          config.headers = config.headers || {}

          // Get token from localStorage
          const accessToken = this.getToken()

          // If token is present add it to request's Authorization Header
          if (accessToken) {
            // eslint-disable-next-line no-param-reassign
            config.headers['x-bpsh-connect-token'] = accessToken
          }
          // eslint-disable-next-line no-param-reassign
          config.headers['x-bpsh-connect-api-key'] = this.getApiKey()
          // eslint-disable-next-line no-param-reassign
          if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            delete config.headers['Content-Type']
          } else {
            config.headers['Content-Type'] = 'application/json'
          }

          return config
        },
        error => Promise.reject(error),
      )

      // Add request/response interceptor
      this.axiosIns.interceptors.response.use(
        response => response,
        error => {
          // const { config, response: { status } } = error
          const { response } = error

          // if (status === 401) {
          // 401'de hemen logout etme, önce /me ile token'ı doğrula
          if (response && response.status === 401 && !(error.config && error.config.skipAuthCheck)) {
            this.verifyTokenOrLogout()
          }
          return Promise.reject(error)
        },
      )
    }

    // Aynı anda gelen birden fazla 401 için tek /me isteği at
    verifyTokenOrLogout() {
      if (this.authCheckPromise) return this.authCheckPromise

      this.authCheckPromise = this.axiosIns.get(this.jwtConfig.me, { skipAuthCheck: true })
        .then(() => {
          // Token hâlâ geçerli - geçici bir 401, logout yapma
          console.warn('401 alındı ama /me başarılı - logout yapılmadı')
        })
        .catch(meError => {
          // Sadece /me de 401 dönerse logout (ağ hatası vb. durumlarda logout yapma)
          if (meError.response && meError.response.status === 401) {
            this.logout()
          }
        })
        .finally(() => {
          this.authCheckPromise = null
        })

      return this.authCheckPromise
    }

    // onAccessTokenFetched(accessToken) {
    //   this.subscribers = this.subscribers.filter(callback => callback(accessToken))
    // }

    // addSubscriber(callback) {
    //   this.subscribers.push(callback)
    // }

    getToken() {
      return localStorage.getItem(this.jwtConfig.storageTokenKeyName) || localStorage.getItem('tempLoginToken')
    }

    getApiKey() {
      return localStorage.getItem('api-key') || this.jwtConfig.apiKey
    }

    // getRefreshToken() {
    //   return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
    // }

    setToken(value) {
      localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
    }

    setApiKey(value) {
      localStorage.setItem('api-key', value)
    }

    // setRefreshToken(value) {
    //   localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
    // }

    login(...args) {
      return this.axiosIns.post(this.jwtConfig.login, ...args)
    }

    emailLogin(...args) {
      return this.axiosIns.post(this.jwtConfig.emailLogin, ...args)
    }

    forgotPassword(phone, lang = 'tr') {
      return this.axiosIns.get(`${this.jwtConfig.requestPassword.replace('{{phoneNumber}}', phone)}?lang=${lang}`)
    }

    forgotPasswordEmail(data) {
      return this.axiosIns.post(this.jwtConfig.requestPasswordEmail, data)
    }

    checkUsername(...args) {
      return this.axiosIns.post(this.jwtConfig.usernameValidation, ...args)
    }

    phoneVerification(phone, lang = 'tr') {
      return this.axiosIns.post(`${this.jwtConfig.phoneVerification.replace('{{phoneNumber}}', phone)}`, { lang })
    }

    getInterests() {
      return this.axiosIns.get(this.jwtConfig.interests)
    }

    phoneCheck(phone, lang = 'tr') {
      return this.axiosIns.post(`${this.jwtConfig.phoneCheck.replace('{{phoneNumber}}', phone)}`, { lang })
    }

    register(...args) {
      return this.axiosIns.post(this.jwtConfig.register, ...args)
    }

    settingsRegister(...args) {
      return this.axiosIns.post(this.jwtConfig.settingsRegister, ...args)
    }

    emailRegister(...args) {
      return this.axiosIns.post(this.jwtConfig.emailRegister, ...args)
    }

    loggedUsers(offset = 0, limit = 100, ...args) {
      const replacedEndpoint = this.jwtConfig.loggedUsers.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.post(replacedEndpoint, ...args)
    }

    usersList(offset = 0, limit = 100, type = 1, ...args) {
      let replacedEndpoint = this.jwtConfig.usersList
        .replace('{{type}}', type.toString())

      if (offset || offset === 0) replacedEndpoint = replacedEndpoint.replace('{{offset}}', offset.toString())
      else replacedEndpoint = replacedEndpoint.replace('/{{offset}}', '')

      if (limit) replacedEndpoint = replacedEndpoint.replace('{{limit}}', limit.toString())
      else replacedEndpoint = replacedEndpoint.replace('/{{limit}}', '')

      return this.axiosIns.post(replacedEndpoint, ...args)
    }

    usersCallListPermitted() {
      return this.axiosIns.get(this.jwtConfig.usersCallListPermitted)
    }

    bonusCampaignCheck() {
      return this.axiosIns.get(this.jwtConfig.bonusCampaignCheck)
    }

    bonusCampaignUserList(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.bonusCampaignUserList.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())

      return this.axiosIns.get(replacedEndpoint)
    }

    bonusOutcallCheck() {
      return this.axiosIns.get(this.jwtConfig.bonusOutcallCheck)
    }

    bonusOutcallUsers(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.bonusOutcallUsers.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())

      return this.axiosIns.get(replacedEndpoint)
    }

    userTriggeredFreeMessage(data) {
      return this.axiosIns.post(this.jwtConfig.userTriggeredFreeMessage, data)
    }

    checkinPopupData() {
      return this.axiosIns.post(this.jwtConfig.checkinPopupData)
    }

    checkinPopupDataConfirm(data) {
      return this.axiosIns.post(this.jwtConfig.checkinPopupDataConfirm, data)
    }

    specialContentsAvailability() {
      return this.axiosIns.get(this.jwtConfig.specialContentsAvailability)
    }

    specialContents(offset = 0, limit = 20) {
      const replacedEndpoint = this.jwtConfig.specialContents
        .replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())

      return this.axiosIns.get(replacedEndpoint)
    }

    specialContentCreate(data) {
      return this.axiosIns.post(this.jwtConfig.specialContentCreate, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }

    specialContentDelete(id) {
      return this.axiosIns.delete(this.jwtConfig.specialContentDelete.replace('{{id}}', id))
    }

    nearByRegions() {
      return this.axiosIns.get(this.jwtConfig.nearByRegions)
    }

    usersListAutoComplete(...args) {
      return this.axiosIns.post(this.jwtConfig.usersListAutoComplete, ...args)
    }

    getUser(id) {
      return this.axiosIns.get(this.jwtConfig.getUser.replace('{{id}}', id))
    }

    // refreshToken() {
    //   return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
    //     refreshToken: this.getRefreshToken(),
    //   })
    // }

    // eslint-disable-next-line class-methods-use-this
    logout() {
      // Remove user from localStorage
      // ? You just removed token from localStorage. If you like, you can also make API call to backend to blacklist used token
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

      // Firebase Analytics Event
      if (window.firebaseAnalytics) {
        logEvent(window.firebaseAnalytics, 'web_user_logout')
      }

      // Remove user from localStorage
      localStorage.removeItem('user')

      // Reset ability
      // this.$ability.update(initialAbility)

      // Redirect to login page
      router.push({ name: 'auth-login' })
      window.location.reload()

      // this.axiosIns.post(this.jwtConfig.logout)
    }

    favoriteUsers(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.favoriteUsers.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    addFavoriteUser(userId) {
      return this.axiosIns.post(this.jwtConfig.addFavoriteUser.replace('{{userUuid}}', userId))
    }

    removeFavoriteUser(userId) {
      return this.axiosIns.delete(this.jwtConfig.removeFavoriteUser.replace('{{userUuid}}', userId))
    }

    callUser(userId) {
      return this.axiosIns.post(this.jwtConfig.callUser.replace('{{userUuid}}', userId))
    }

    // AI sesli görüşme başlat (LiveKit). Hedef AI kullanıcı uuid + arayan dili gönderilir.
    startAiVoiceCall(aiUserUuid, language) {
      return this.axiosIns.post(this.jwtConfig.startAiVoiceCall, { ai_user_uuid: aiUserUuid, language })
    }

    appCall(userId, data = {}) {
      return this.axiosIns.post(this.jwtConfig.appCall.replace('{{userUuid}}', userId), data)
    }

    appVideoCall(userId, data = {}) {
      return this.axiosIns.post(this.jwtConfig.appVideoCall.replace('{{userUuid}}', userId), data)
    }

    appSwitchCallType(data = {}) {
      return this.axiosIns.post(this.jwtConfig.appSwitchCallType, data)
    }

    appCallCancel(data) {
      return this.axiosIns.post(this.jwtConfig.appCallCancel, data)
    }

    heartBeat(data) {
      return this.axiosIns.post(this.jwtConfig.heartBeat, data)
    }

    getSettings() {
      return this.axiosIns.get(this.jwtConfig.getSettings)
    }

    updateSettings(data) {
      return this.axiosIns.put(this.jwtConfig.updateSettings, data)
    }

    updateLanguage(lang) {
      return this.axiosIns.put(this.jwtConfig.updateLanguage.replace('{{lang}}', lang))
    }

    getMe(token = null) {
      if (token) {
        return this.axiosIns.get(this.jwtConfig.me, {
          headers: {
            'x-bpsh-connect-token': token,
          },
        })
      }
      return this.axiosIns.get(this.jwtConfig.me)
    }

    getMyInterests() {
      return this.axiosIns.get(this.jwtConfig.myInterests)
    }

    updateMyInterests(data, token = null) {
      if (token) {
        return this.axiosIns.post(this.jwtConfig.updateMyInterests, data, {
          headers: {
            'x-bpsh-connect-token': token,
          },
        })
      }
      return this.axiosIns.post(this.jwtConfig.updateMyInterests, data)
    }

    updateMe(data, token = null) {
      if (token) {
        return this.axiosIns.put(this.jwtConfig.updateMe, data, {
          headers: {
            'x-bpsh-connect-token': token,
          },
        })
      }
      return this.axiosIns.put(this.jwtConfig.updateMe, data)
    }

    updateProfile(data, token = null) {
      if (token) {
        return this.axiosIns.put(this.jwtConfig.updateProfile, data, {
          headers: {
            'x-bpsh-connect-token': token,
          },
        })
      }
      return this.axiosIns.put(this.jwtConfig.updateProfile, data)
    }

    updatePhone(phone) {
      return this.axiosIns.put(this.jwtConfig.updateUserPhone.replace('{{phoneNumber}}', phone))
    }

    putPendingUser(data) {
      return this.axiosIns.put(this.jwtConfig.putPendingUser, data)
    }

    getNotifications(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.notifications.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    unReadNotificationsCount() {
      return this.axiosIns.get(this.jwtConfig.unReadNotificationsCount)
    }

    markAllNotificationsAsRead() {
      return this.axiosIns.put(this.jwtConfig.markAllNotificationsAsRead)
    }

    markOneNotificationAsRead(id) {
      return this.axiosIns.put(this.jwtConfig.markOneNotificationAsRead.replace('{{notificationUuid}}', id))
    }

    updateSearchCriteria(data, token = null) {
      if (token) {
        return this.axiosIns.put(this.jwtConfig.updateSearchCriteria, data, {
          headers: {
            'x-bpsh-connect-token': token,
          },
        })
      }
      return this.axiosIns.put(this.jwtConfig.updateSearchCriteria, data)
    }

    removeAccount(id) {
      return this.axiosIns.delete(this.jwtConfig.removeAccount.replace('{{reasonTypeId}}', id))
    }

    getReasons() {
      return this.axiosIns.get(this.jwtConfig.getReasons)
    }

    discoverList(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.discoverList.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getChatUsers(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.chatUserList.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getChatUsers2(offset = 0, limit = 100, { sort = null, moodId = null } = {}) {
      const replacedEndpoint = this.jwtConfig.chatUserList2.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      const params = {}
      if (sort) params.sort = sort
      if (moodId) params.mood_id = moodId
      return this.axiosIns.get(replacedEndpoint, { params })
    }

    getChat(userId, offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getChat.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
        .replace('{{userUuid}}', userId.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getChat2(userId, offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getChat2.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
        .replace('{{userUuid}}', userId.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    chatMarkSeen(userId) {
      return this.axiosIns.post(this.jwtConfig.chatMarkSeen, { uuid: userId })
    }

    chatMarkSeen2(userId) {
      return this.axiosIns.post(this.jwtConfig.chatMarkSeen2, { uuid: userId })
    }

    chatDeleteHistory(userId) {
      return this.axiosIns.post(this.jwtConfig.chatDeleteHistory, { uuid: userId })
    }

    getChatCommunityUsers(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.chatCommunityUsers.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getChatCommunityMoods() {
      return this.axiosIns.get(this.jwtConfig.chatCommunityMoods)
    }

    getChatCommunityMyMood() {
      return this.axiosIns.get(this.jwtConfig.chatCommunityMyMood)
    }

    chatUnlockAiImage(msgId) {
      return this.axiosIns.post(this.jwtConfig.chatUnlockAiImage, { msg_id: msgId })
    }

    /**
     * Guul oyun oturumu açar.
     * Doküman: is_join yalnızca chat mesajından katılırken gönderilir (hak/kredi tüketmez);
     * oyunu başlatırken is_used_credits gönderilir.
     */
    guulMatch(opponentUserId, { isUsedCredits = false, isJoin = false } = {}) {
      const body = { opponentUserId }

      if (isJoin) body.is_join = true
      else body.is_used_credits = isUsedCredits

      return this.axiosIns.post(this.jwtConfig.guulMatch, body)
    }

    chatMediaRequest(toUserUuid, mediaType) {
      return this.axiosIns.post(this.jwtConfig.chatMediaRequest, {
        to_user_uuid: toUserUuid,
        media_type: mediaType,
      })
    }

    chatMediaRequestFulfill(requestId, formData) {
      return this.axiosIns.post(
        this.jwtConfig.chatMediaRequestFulfill.replace('{{requestId}}', requestId),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
    }

    // Doküman gövdesiz POST diyor; is_used_credits backend yok sayarsa zararsız,
    // iki fazlı bir akış bekliyorsa da "elmasla satın al"ı doğru şekilde bildirir.
    chatMediaRequestUnlock(requestId, isUsedCredits = true) {
      return this.axiosIns.post(
        this.jwtConfig.chatMediaRequestUnlock.replace('{{requestId}}', requestId),
        { is_used_credits: isUsedCredits },
      )
    }

    updateChatCommunityMood(moodId) {
      return this.axiosIns.put(this.jwtConfig.chatCommunityMyMood, { mood_id: moodId })
    }

    getGifts() {
      return this.axiosIns.get(this.jwtConfig.getGifts)
    }

    getSentGifts(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getSentGifts.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getSentGifts2(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getSentGifts2.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getReceivedGifts(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getReceivedGifts.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getReceivedGifts2(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getReceivedGifts2.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getSentGiftMedias(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getSentGiftMedias.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getReceivedGiftMedias(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getReceivedGiftMedias.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getMultimediaCredits() {
      return this.axiosIns.get(this.jwtConfig.getMultimediaCredits)
    }

    accountCredits() {
      return this.axiosIns.get(this.jwtConfig.accountCredits)
    }

    accountEarnedCredits(data = {}) {
      return this.axiosIns.post(this.jwtConfig.accountEarnedCredits, data)
    }

    updateOnlineStatus(status) {
      return this.axiosIns.put(this.jwtConfig.updateOnlineStatus.replace('{{status}}', status))
    }

    lpUserAction(data) {
      return this.axiosIns.post(this.jwtConfig.lpUserAction, data)
    }

    getLandingProducts() {
      return this.axiosIns.get(this.jwtConfig.getLandingProducts)
    }

    getProducts() {
      return this.axiosIns.get(this.jwtConfig.getProducts)
    }

    getChatProducts() {
      return this.axiosIns.get(this.jwtConfig.getChatProducts)
    }

    buyChatProduct(data) {
      return this.axiosIns.post(this.jwtConfig.buyChatProduct, data)
    }

    updateCallType(type) {
      return this.axiosIns.put(this.jwtConfig.updateCallType.replace('{{type}}', type.toString()))
    }

    getBlockedUsers(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getBlockedUsers.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getReverseFavorites(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getReverseFavorites.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getCalls(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getCalls.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    getCallHistory(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.getCallHistory.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())
      return this.axiosIns.get(replacedEndpoint)
    }

    sendGift(data) {
      return this.axiosIns.post(this.jwtConfig.sendGift, data)
    }

    getMedias(userId) {
      return this.axiosIns.get(this.jwtConfig.getMedias.replace('{{ownId}}', userId))
    }

    getImages(userId) {
      return this.axiosIns.get(this.jwtConfig.getImages.replace('{{ownId}}', userId))
    }

    getVideos(userId) {
      return this.axiosIns.get(this.jwtConfig.getVideos.replace('{{ownId}}', userId))
    }

    uploadImage(data) {
      return this.axiosIns.post(this.jwtConfig.uploadImage, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }

    uploadVideo(data) {
      return this.axiosIns.post(this.jwtConfig.uploadVideo, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }

    uploadBondingVideo(file, onProgress) {
      return new Promise((resolve, reject) => {
        const uploadFile = file.type === 'video/mp4' ? file : new File([file], 'bonding-video.mp4', { type: 'video/mp4' })
        const formData = new FormData()
        formData.append('file', uploadFile, 'bonding-video.mp4')
        formData.append('type', 'bonding')

        const xhr = new XMLHttpRequest()
        xhr.open('POST', this.jwtConfig.uploadBondingVideo)
        if (this.getToken()) xhr.setRequestHeader('x-bpsh-connect-token', this.getToken())
        xhr.setRequestHeader('x-bpsh-connect-api-key', this.getApiKey())

        xhr.upload.onprogress = event => {
          if (!event.lengthComputable || typeof onProgress !== 'function') return
          onProgress(Math.round((event.loaded / event.total) * 100))
        }

        xhr.onload = () => {
          let json = null

          try {
            json = JSON.parse(xhr.responseText)
          } catch (e) {
            json = null
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ data: json })
            return
          }

          const message = json?.header?.error?.message || json?.message || 'Yükleme sırasında hata oluştu'
          reject(new Error(message))
        }

        xhr.onerror = () => {
          reject(new Error('Yükleme sırasında bağlantı hatası oluştu'))
        }

        xhr.send(formData)
      })
    }

    removeImage(id) {
      return this.axiosIns.delete(this.jwtConfig.removeImage.replace('{{imageId}}', id))
    }

    removeVideo(id) {
      return this.axiosIns.delete(this.jwtConfig.removeVideo.replace('{{videoId}}', id))
    }

    toggleImageStatus(id) {
      return this.axiosIns.put(this.jwtConfig.toggleImageStatus.replace('{{imageId}}', id))
    }

    setUserProfileImage(id) {
      return this.axiosIns.put(this.jwtConfig.setUserProfileImage.replace('{{imageId}}', id))
    }

    paymentPostnetInit(data) {
      return this.axiosIns.post(this.jwtConfig.paymentPostnetInit, data)
    }

    paymentPostnetPerform(data) {
      return this.axiosIns.post(this.jwtConfig.paymentPostnetPerform, data)
    }

    paymentCraftgateInit(data) {
      return this.axiosIns.post(this.jwtConfig.paymentCraftgateInit, data)
    }

    paymentCraftgateFinalize(data) {
      return this.axiosIns.post(this.jwtConfig.paymentCraftgateFinalize, data)
    }

    checkOneTimeOffer(data) {
      return this.axiosIns.post(this.jwtConfig.checkOneTimeOffer, data)
    }

    checkBonusCredits(data) {
      return this.axiosIns.post(this.jwtConfig.checkBonusCredits, data)
    }

    blockUser(userId) {
      return this.axiosIns.post(this.jwtConfig.blockUser.replace('{{userUuid}}', userId))
    }

    unBlockUser(userId) {
      return this.axiosIns.delete(this.jwtConfig.unBlockUser.replace('{{userUuid}}', userId))
    }

    reportUser(data) {
      return this.axiosIns.post(this.jwtConfig.reportUser, data)
    }

    reportUser2(data) {
      return this.axiosIns.post(this.jwtConfig.reportUser2, data)
    }

    support(data) {
      return this.axiosIns.post(this.jwtConfig.support, data)
    }

    loginWithApple(data) {
      return this.axiosIns.post(this.jwtConfig.loginApple, data)
    }

    loginWithFacebook(data) {
      return this.axiosIns.post(this.jwtConfig.loginFacebook, data)
    }

    loginWithGoogle(data) {
      return this.axiosIns.post(this.jwtConfig.loginGoogle, data)
    }

    getCashouts() {
      return this.axiosIns.get(this.jwtConfig.getCashouts)
    }

    postCashout(data = null) {
      return this.axiosIns.post(this.jwtConfig.postCashout, data)
    }

    postSupportCashout(data = null) {
      return this.axiosIns.post(this.jwtConfig.postSupportCashout, data)
    }

    postPaymentsCashout(data = null) {
      return this.axiosIns.post(this.jwtConfig.postPaymentsCashout, data)
    }

    postPaymentsUserCashoutDetails(data = {}) {
      return this.axiosIns.post(this.jwtConfig.postPaymentsUserCashoutDetails, data)
    }

    paybymePurchase(data) {
      return this.axiosIns.post(this.jwtConfig.paybymePurchase, data)
    }

    phoneNumberUpdate(data) {
      return this.axiosIns.put(this.jwtConfig.phoneNumberUpdate, data)
    }

    phoneNumberVerification(data) {
      return this.axiosIns.put(this.jwtConfig.phoneNumberVerification, data)
    }

    emailUpdate(data) {
      return this.axiosIns.put(this.jwtConfig.emailUpdate, data)
    }

    emailVerification(data) {
      return this.axiosIns.post(this.jwtConfig.emailVerification, data)
    }

    generateUsername() {
      return this.axiosIns.get(this.jwtConfig.generateUsername)
    }

    emailUpdateVerification(data) {
      return this.axiosIns.put(this.jwtConfig.emailUpdateVerification, data)
    }

    postLogout() {
      return this.axiosIns.post(this.jwtConfig.logout)
    }

    updateCameraStatus(status) {
      return this.axiosIns.put(this.jwtConfig.updateCameraStatus.replace('{{status}}', status))
    }

    creditsCharged() {
      return this.axiosIns.get(this.jwtConfig.creditsCharged)
    }

    campaigns() {
      return this.axiosIns.get(this.jwtConfig.campaigns)
    }

    campaigns2() {
      return this.axiosIns.get(this.jwtConfig.campaigns2)
    }

    campaignsDisplayed(data) {
      return this.axiosIns.post(this.jwtConfig.campaignsDisplayed, data)
    }

    couponCodeToAddDiamond(data) {
      return this.axiosIns.post(this.jwtConfig.couponCodeToAddDiamond, data)
    }

    couponCodeCheckBeforePurchasing(data) {
      return this.axiosIns.post(this.jwtConfig.couponCodeCheckBeforePurchasing, data)
    }

    // v2 API
    checkNudity(data) {
      return this.axiosIns.post(this.jwtConfig.checkNudity, data)
    }

    getPosts(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.posts.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())

      return this.axiosIns.get(replacedEndpoint)
    }

    getPostsForYou(offset = 0, limit = 100) {
      const replacedEndpoint = this.jwtConfig.postsForYou.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())

      return this.axiosIns.get(replacedEndpoint)
    }

    getPostsDiscoveryOpen() {
      return this.axiosIns.get(this.jwtConfig.postsDiscoveryOpen)
    }

    getPostsDiscovery(offset = 0, limit = 50) {
      const replacedEndpoint = this.jwtConfig.postsDiscovery.replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())

      return this.axiosIns.get(replacedEndpoint)
    }

    likePost(data) {
      return this.axiosIns.post(this.jwtConfig.likePost, data)
    }

    likePostDelete(id) {
      return this.axiosIns.delete(this.jwtConfig.likePostDelete.replace('{{id}}', id))
    }

    deletePost(id) {
      return this.axiosIns.delete(this.jwtConfig.deletePost.replace('{{id}}', id))
    }

    getUserPosts(uuid, offset = 0, limit = 20) {
      const replacedEndpoint = this.jwtConfig.userPosts
        .replace('{{uuid}}', uuid)
        .replace('{{offset}}', offset.toString())
        .replace('{{limit}}', limit.toString())

      return this.axiosIns.get(replacedEndpoint)
    }

    newPost(data) {
      return this.axiosIns.post(this.jwtConfig.newPost, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }

    missionsActive() {
      return this.axiosIns.get(this.jwtConfig.missionsActive)
    }

    incentiveDailyNewTask(date) {
      return this.axiosIns.get(this.jwtConfig.incentiveDailyNewTask.replace('{{date}}', date))
    }

    newDailyTasks(type) {
      return this.axiosIns.get(this.jwtConfig.newDailyTasks.replace('{{type}}', type))
    }

    newDailyTasksList(type, offset = 0, limit = 20) {
      return this.axiosIns.get(
        this.jwtConfig.newDailyTasksList
          .replace('{{type}}', type)
          .replace('{{offset}}', offset)
          .replace('{{limit}}', limit),
      )
    }

    newDailyTasksPendingChatList(offset = 0, limit = 20) {
      return this.axiosIns.get(
        this.jwtConfig.newDailyTasksPendingChatList
          .replace('{{offset}}', offset)
          .replace('{{limit}}', limit),
      )
    }

    newDailyTasksEligibleList() {
      return this.axiosIns.get(this.jwtConfig.newDailyTasksEligibleList)
    }

    newDailyTasksProcessUsers() {
      return this.axiosIns.get(this.jwtConfig.newDailyTasksProcessUsers)
    }

    missions(url, date = null) {
      const replacedUrl = this.jwtConfig.missions
        .replace('{{url}}', url)

      if (date) {
        return this.axiosIns.get(replacedUrl.replace('{{date}}', date))
      }
      return this.axiosIns.get(replacedUrl.replace('/{{date}}', ''))
    }

    missionsCollectedBeans(date) {
      return this.axiosIns.get(this.jwtConfig.missionsCollectedBeans.replace('{{date}}', date))
    }

    missionsConnectedTime(date) {
      return this.axiosIns.get(this.jwtConfig.missionsConnectedTime.replace('{{date}}', date))
    }

    missionsMomentMessageTime(date) {
      return this.axiosIns.get(this.jwtConfig.missionsMomentMessageTime.replace('{{date}}', date))
    }

    missionsCallCount(date) {
      return this.axiosIns.get(this.jwtConfig.missionsCallCount.replace('{{date}}', date))
    }

    missionsNdc(date) {
      return this.axiosIns.get(this.jwtConfig.missionsNdc.replace('{{date}}', date))
    }

    faceDetection(data) {
      return this.axiosIns.post(this.jwtConfig.faceDetection, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }

    faceDetect(data) {
      return this.axiosIns.post(this.jwtConfig.faceDetect, data)
    }

    postFemaleHostApplications(data) {
      return this.axiosIns.post(this.jwtConfig.postFemaleHostApplications, data)
    }

    checkPwaFirstSetup() {
      return this.axiosIns.get(this.jwtConfig.checkPwaFirstSetup)
    }

    pwaFirstSetup() {
      return this.axiosIns.get(this.jwtConfig.pwaFirstSetup)
    }

    getVoiceRoomGifts() {
      return this.axiosIns.get(this.jwtConfig.getVoiceRoomGifts)
    }

    publishVoiceRoom(data) {
      return this.axiosIns.post(this.jwtConfig.publishVoiceRoom, data)
    }

    sendVoiceRoomGift(data) {
      return this.axiosIns.post(this.jwtConfig.sendVoiceRoomGift, data)
    }

    sendVoiceRoomHeartbeat(data) {
      return this.axiosIns.post(this.jwtConfig.sendVoiceRoomHeartbeat)
    }

    endVoiceRoom(data) {
      return this.axiosIns.post(this.jwtConfig.endVoiceRoom)
    }

    subscribeVoiceRoom(channelId) {
      return this.axiosIns.post(this.jwtConfig.subscribeVoiceRoom.replace('{{channelId}}', channelId))
    }

    getVoiceRoomPublishers(channelId) {
      return this.axiosIns.get(this.jwtConfig.getVoiceRoomPublishers.replace('{{channelId}}', channelId))
    }

    sendVoiceRoomBePublisher(data) {
      return this.axiosIns.post(this.jwtConfig.sendVoiceRoomBePublisher)
    }

    approveSpeakerRequest(uuid) {
      return this.axiosIns.post(this.jwtConfig.approveSpeakerRequest.replace('{{uuid}}', uuid))
    }

    rejectSpeakerRequest(uuid) {
      return this.axiosIns.post(this.jwtConfig.rejectSpeakerRequest.replace('{{uuid}}', uuid))
    }

    getListOfVoiceRoomParticipants(channelId) {
      return this.axiosIns.get(this.jwtConfig.getListOfVoiceRoomParticipants.replace('{{channelId}}', channelId))
    }

    getGoalsOfVoiceRoom(channelId) {
      return this.axiosIns.get(this.jwtConfig.getGoalsOfVoiceRoom.replace('{{channelId}}', channelId))
    }

    kickUserFromVoiceRoom(uuid) {
      return this.axiosIns.post(this.jwtConfig.kickUserFromVoiceRoom.replace('{{uuid}}', uuid))
    }

    toggleMuteVoiceRoom(channelId, uuid) {
      return this.axiosIns.get(this.jwtConfig.toggleMuteVoiceRoom.replace('{{channelId}}', channelId).replace('{{uuid}}', uuid))
    }

    getReceivedGiftsVoiceRoom(channelId) {
      return this.axiosIns.get(this.jwtConfig.getReceivedGiftsVoiceRoom.replace('{{channelId}}', channelId))
    }

    // Video Rooms

    getVideoRoomGifts() {
      return this.axiosIns.get(this.jwtConfig.getVideoRoomGifts)
    }

    publishVideoRoom(data) {
      return this.axiosIns.post(this.jwtConfig.publishVideoRoom, data)
    }

    sendVideoRoomGift(data) {
      return this.axiosIns.post(this.jwtConfig.sendVideoRoomGift, data)
    }

    sendVideoRoomHeartbeat(data) {
      return this.axiosIns.post(this.jwtConfig.sendVideoRoomHeartbeat)
    }

    endVideoRoom(data) {
      return this.axiosIns.post(this.jwtConfig.endVideoRoom)
    }

    subscribeVideoRoom(channelId, publisherId) {
      return this.axiosIns.post(this.jwtConfig.subscribeVideoRoom.replace('{{channelId}}', channelId).replace('{{publisherId}}', publisherId))
    }

    getVideoRoomPublishers() {
      return this.axiosIns.get(this.jwtConfig.getVideoRoomPublishers)
    }

    sendVideoRoomBePublisher(data) {
      return this.axiosIns.post(this.jwtConfig.sendVideoRoomBePublisher)
    }

    approveSpeakerRequestVideoRoom(uuid) {
      return this.axiosIns.post(this.jwtConfig.approveSpeakerRequestVideoRoom.replace('{{uuid}}', uuid))
    }

    rejectSpeakerRequestVideoRoom(uuid) {
      return this.axiosIns.post(this.jwtConfig.rejectSpeakerRequestVideoRoom.replace('{{uuid}}', uuid))
    }

    getListOfVideoRoomParticipants(channelId, publisherId) {
      return this.axiosIns.get(this.jwtConfig.getListOfVideoRoomParticipants.replace('{{channelId}}', channelId).replace('{{publisherId}}', publisherId))
    }

    getGoalsOfVideoRoom(channelId) {
      return this.axiosIns.get(this.jwtConfig.getGoalsOfVideoRoom.replace('{{channelId}}', channelId))
    }

    kickUserFromVideoRoom(uuid) {
      return this.axiosIns.post(this.jwtConfig.kickUserFromVideoRoom.replace('{{uuid}}', uuid))
    }

    toggleMuteVideoRoom(channelId, uuid) {
      return this.axiosIns.get(this.jwtConfig.toggleMuteVideoRoom.replace('{{channelId}}', channelId).replace('{{uuid}}', uuid))
    }

    getReceivedGiftsVideoRoom(channelId, publisherId) {
      return this.axiosIns.get(this.jwtConfig.getReceivedGiftsVideoRoom.replace('{{channelId}}', channelId).replace('{{publisherId}}', publisherId))
    }

    // Male User Tasks
    maleUserTasksWeeklyStatus(testDate = null) {
      let url = this.jwtConfig.maleUserTasksWeeklyStatus
      if (testDate) {
        url += `?test_date=${testDate}`
      }
      return this.axiosIns.get(url)
    }

    maleUserTasksCheckIn() {
      return this.axiosIns.post(this.jwtConfig.maleUserTasksCheckIn)
    }

    maleUserTasksConvertWeekly(testDate = null) {
      let url = this.jwtConfig.maleUserTasksConvertWeekly
      if (testDate) {
        url += `?test_date=${testDate}`
      }
      return this.axiosIns.post(url)
    }

    bohoClubListTypes() {
      return this.axiosIns.get(this.jwtConfig.bohoClubListTypes)
    }

    bohoClubPublishers() {
      return this.axiosIns.get(this.jwtConfig.bohoClubPublishers)
    }

    userInformationSha256() {
      return this.axiosIns.get(this.jwtConfig.userInformationSha256)
    }

    referralMe() {
      return this.axiosIns.get(this.jwtConfig.referralMe)
    }

    referralInvitees(offset = 0) {
      return this.axiosIns.get(this.jwtConfig.referralInvitees, { params: { offset } })
    }

    referralClaim(code, method = 'web') {
      return this.axiosIns.post(this.jwtConfig.referralClaim, { code, method })
    }

    claimStoredReferral() {
      try {
        const cookie = document.cookie.match(/(?:^|; )referral_code=([^;]+)/)
        if (!cookie) {
          console.info('[REFERRAL] claim skipped: no referral cookie')
          return
        }
        const code = decodeURIComponent(cookie[1])
        if (!/^[A-Za-z0-9]{6}$/.test(code)) {
          console.warn('[REFERRAL] claim skipped: invalid cookie code')
          return
        }
        console.info('[REFERRAL] sending web claim')
        this.referralClaim(code, 'web').then(() => {
          console.info('[REFERRAL] web claim succeeded')
          document.cookie = 'referral_code=; Max-Age=0; Path=/'
        }).catch(error => {
          console.warn('[REFERRAL] web claim failed', error.response ? error.response.status : 'network error', error.response ? error.response.data : '')
        })
      } catch (error) {
        console.warn('[REFERRAL] claim preparation failed', error.message)
      }
    }
}
